/**
 * Client-side transaction history backed by localStorage.
 * Records wallet sends, transfers, and bridges with live status tracking.
 *
 * Dispatches CustomEvent('phnm-tx-update') whenever data changes so that
 * React hooks can re-render without polling.
 */

const STORAGE_KEY = 'phnm-tx-history';
const MAX_TRANSACTIONS = 50;
/** Transactions stuck in processing/confirming for longer than this are marked failed */
const STALE_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes

export const TX_UPDATE_EVENT = 'phnm-tx-update';

export type TxType = 'send' | 'nft-transfer' | 'bridge';

export type TxStatus = 'processing' | 'confirming' | 'confirmed' | 'failed';

export interface WalletTransaction {
  id: string;
  type: TxType;
  token: string;
  amount: string;
  from: string;
  to: string;
  txHash: string | null;
  explorerUrl: string | null;
  // Post CHIP migration this is effectively `'base'`. The 'polygon' literal
  // is retained until the legacy bridge / USDT-redemption flows are removed
  // by the cross-chain decom workstream.
  chain: 'base' | 'polygon';
  gasless: boolean;
  /** Unix timestamp in seconds */
  timestamp: number;
  status: TxStatus;
  /** Relay/send ID for gasless transactions (used for polling) */
  relayId?: string;
  /** Error message if status is 'failed' */
  errorMessage?: string;
}

function dispatchUpdate(): void {
  window.dispatchEvent(new CustomEvent(TX_UPDATE_EVENT));
}

export function getTransactions(): WalletTransaction[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return [];
    const parsed = JSON.parse(stored) as WalletTransaction[];
    // Backward compat: old transactions without status are confirmed
    return parsed.map((tx) => ({ ...tx, status: tx.status ?? 'confirmed' }));
  } catch {
    return [];
  }
}

export function addTransaction(
  tx: Omit<WalletTransaction, 'id' | 'timestamp' | 'status'> & { status?: TxStatus }
): WalletTransaction {
  const history = getTransactions();
  const newTx: WalletTransaction = {
    ...tx,
    status: tx.status ?? 'confirmed',
    id: `tx-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    timestamp: Math.floor(Date.now() / 1000),
  };
  history.unshift(newTx);
  if (history.length > MAX_TRANSACTIONS) history.length = MAX_TRANSACTIONS;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(history));
  dispatchUpdate();
  return newTx;
}

export function updateTransaction(
  id: string,
  patch: Partial<Pick<WalletTransaction, 'status' | 'txHash' | 'explorerUrl' | 'errorMessage'>>
): void {
  const history = getTransactions();
  const idx = history.findIndex((tx) => tx.id === id);
  if (idx === -1) return;
  history[idx] = { ...history[idx], ...patch };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(history));
  dispatchUpdate();
}

/**
 * Mark transactions stuck in processing/confirming for >10 minutes as failed.
 * Called once on app mount to clean up after page refreshes mid-transaction.
 */
export function markStaleFailed(): void {
  const history = getTransactions();
  const now = Date.now();
  let changed = false;

  for (const tx of history) {
    if (
      (tx.status === 'processing' || tx.status === 'confirming') &&
      now - tx.timestamp * 1000 > STALE_TIMEOUT_MS
    ) {
      tx.status = 'failed';
      tx.errorMessage = 'Transaction timed out';
      changed = true;
    }
  }

  if (changed) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(history));
    dispatchUpdate();
  }
}

export function clearTransactions(): void {
  localStorage.removeItem(STORAGE_KEY);
  dispatchUpdate();
}
